import { MutationFunction, useQuery } from '@apollo/client';
import { Badge, Button, NavigationTabs, NavigationTabsItem, Pill, Stack, Tooltip } from '@sixfold/common-ui';
import { BackofficeScope } from '@sixfold/session-interface';
import { isNil } from '@sixfold/typed-primitives';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, Route, Routes as RouterRoutes, useParams, useNavigate, useLocation } from 'react-router';

import { CompanyForm } from './company_form';
import { CompanyIdentifierAddModal } from './company_identifier_add_modal';
import { CompanyIdentifierModifyModal } from './company_identifier_modify_modal';
import { CompanyGroupContainer } from '../../company_group/containers/company_group_container';
import { getCompanyKafkaQueryTimespan, KafkaContextProvider } from '../../components/bigquery/common/kafka_context';
import { CompanyKafkaTab } from '../../components/bigquery/company_kafka/company_kafka_tab';
import { FormattedDate } from '../../components/date_formatting/formatted_date';
import { TagsInput } from '../../components/tags_input';
import { useFavoriteCompanies } from '../../home/storage';
import { useHasScopes, useIsAdmin } from '../../lib/authorization';
import {
  CompanyDetailsQuery,
  CompanyTabCountsQuery,
  CompanyTabCountsQueryVariables,
  UpdateCompanyMutation,
  UpdateCompanyMutationVariables,
} from '../../lib/graphql';
import { shipmentsViewUrl, customSlugify } from '../../lib/util/url';
import { MetricsContainer } from '../../metrics/containers/metrics_container';
import { PlatformContainer } from '../../platform/containers/platform_container';
import { PlatformListContainer } from '../../platform/containers/platform_list_container';
import { RelationshipsTabs } from '../../relationships/components/relationships_tabs';
import { RelationshipContainer } from '../../relationships/containers/relationship_container';
import { RelationshipsContainer } from '../../relationships/containers/relationships_container';
import { Routes } from '../../routes';
import { CreateIntegrationContainer } from '../../telematic_integration/containers/create_integration_container';
import { IntegrationContainer } from '../../telematic_integration/containers/integration_container';
import { WebhookSubscriptionContainer } from '../../telematic_integration/containers/webhook_subscription_container';
import { CompanyUserGroupContainer } from '../../user_group_on_platform/containers/company_user_group_container';
import { CompanyUserGroupsContainer } from '../../user_group_on_platform/containers/company_user_groups_container';
import { UserInCompanyContainer, UserInCompanyListContainer } from '../../user_in_company/containers';
import { VehicleGrantListContainer } from '../../vehicle_grant/containers/vehicle_grant_list_container';
import { CompanyAgreementContainer } from '../containers/company_agreement_container';
import { CompanyAssetsContainer } from '../containers/company_assets_container';
import { CompanyConfigurationContainer } from '../containers/company_configuration_container';
import { CompanyDashboardsContainer } from '../containers/company_dashboard_container';
import { CompanyFeatureFlagsContainer } from '../containers/company_feature_flags_container';
import { CompanyFileStorageContainer } from '../containers/company_file_storage_container';
import { CompanyIntegrationsContainer } from '../containers/company_integrations';
import { CompanyLemContainer } from '../containers/company_lem_container';
import { CompanyOnboardingContainer } from '../containers/company_onboarding_container';
import { companyTabCountsQuery } from '../graphql';
import { getCompanyToursLinks, mapIdentifierType } from '../utils';
import { LEGACY_ENABLE_VEHICLE_HISTORY_UI_FLAG_ID } from '../vehicle_history_feature_flags';

export interface CompanyViewDataProps {
  data: {
    company?: CompanyDetailsQuery['company'] | null;
    availableTags: CompanyDetailsQuery['availableCompanyTags'];
    identifiers: CompanyIdentifierProps[];
  };
}

const KNOWN_COMPANY_SECTIONS = new Set([
  'users',
  'integrations',
  'platforms',
  'relationships',
  'assets',
  'onboarding',
  'configuration',
  'features',
  'dashboards',
  'kafka',
  'file-storage',
  'lem',
  'agreement',
  'groups',
  'grants',
  'carriers',
  'shippers',
  'edit',
]);

const tabCount = (count: number | undefined) =>
  isNil(count) ? undefined : (
    <Badge size="small" kind="neutral">
      {count}
    </Badge>
  );

type TagMutation = (input: { variables: { input: { companyId: string; tagName: string } } }) => void;

interface CompanyViewProps {
  updateCompany: MutationFunction<UpdateCompanyMutation, UpdateCompanyMutationVariables>;
  onAddCompanyTag: TagMutation;
  onRemoveCompanyTag: TagMutation;
}

export const CompanyView: React.FC<CompanyViewDataProps & CompanyViewProps> = ({
  data: { company, availableTags, identifiers },
  updateCompany,
  onAddCompanyTag,
  onRemoveCompanyTag,
}) => {
  const isAdmin = useIsAdmin();
  const hasCompanyEditPermission = useHasScopes([BackofficeScope.companyWriteCompany]);
  const isPrivilegedUser = isAdmin || hasCompanyEditPermission;
  const params = useParams<{ company_id: string; slug_company_name?: string; '*'?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [favoriteCompanies, toggleFavoriteCompany] = useFavoriteCompanies();
  const tabCountsResult = useQuery<CompanyTabCountsQuery, CompanyTabCountsQueryVariables>(companyTabCountsQuery, {
    variables: { company_id: company?.company_id ?? '' },
    skip: isNil(company),
  });
  const tabCounts = tabCountsResult.data;

  if (isNil(company)) {
    return <div>Company not found</div>;
  }

  const {
    company_id,
    company_name,
    is_carrier,
    is_shipper,
    is_pilot,
    is_paying_customer,
    is_test,
    fms_integrated_at,
    isPendingSignup,
    tags,
    signupCompletedAt,
    platforms,
    feature_flags,
  } = company;

  const enabledFeatureFlagsCount = Object.entries(feature_flags ?? {}).filter(
    ([flagId, isEnabled]) => isEnabled === true && flagId !== LEGACY_ENABLE_VEHICLE_HISTORY_UI_FLAG_ID,
  ).length;

  const companyNameSlug = customSlugify(company_name);
  const shipmentsHref = shipmentsViewUrl(company_id);
  const isFavorite = favoriteCompanies.some((favorite) => favorite.companyId === company_id);

  const createTpaButton = (urlKey: keyof (typeof platforms)[0], label: string) => {
    // Please note that if multiple platforms have a URL with the same type, only the first is used (to avoid duplicate buttons)
    const url = platforms?.find((platform) => platform[urlKey])?.[urlKey];
    if (!url) return null;

    return (
      <Button
        key={url}
        type="button"
        kind="primary"
        size="small"
        onClick={() => {
          window.open(url, '_blank', 'noopener,noreferrer');
        }}>
        {label}
      </Button>
    );
  };

  const tpaButton = createTpaButton('tpaProductionUrl', 'Open TPA UI');
  const tpaTestButton = createTpaButton('tpaTestUrl', 'Open TPA UI - AC');
  const tpaIntButton = createTpaButton('tpaIntUrl', 'Open TPA UI - IN');
  const tpaDevButton = createTpaButton('tpaDevUrl', 'Open TPA UI - DV');

  const slugFromUrl = params.slug_company_name;
  const splatFromUrl = params['*'] ?? '';

  // When the URL has no slug, react-router greedily matches the section name
  // into `:slug_company_name?`. Detect and unswap it. The `!== companyNameSlug`
  // check avoids a loop if a company is literally named after a section.
  const slugInUrlIsActuallySection =
    slugFromUrl !== undefined && slugFromUrl !== companyNameSlug && KNOWN_COMPANY_SECTIONS.has(slugFromUrl);
  const slugInUrl = slugInUrlIsActuallySection ? undefined : slugFromUrl;
  const sectionPath = slugInUrlIsActuallySection
    ? `${slugFromUrl}${splatFromUrl ? `/${splatFromUrl}` : ''}`
    : splatFromUrl;

  if (slugInUrl !== companyNameSlug) {
    const slugSegment = `/${companyNameSlug}`;
    const sectionSegment = sectionPath ? `/${sectionPath}` : '';
    const correctedPath = `/companies/${company_id}${slugSegment}${sectionSegment}`;
    return <Navigate to={`${correctedPath}${location.search}${location.hash}`} replace />;
  }

  const { initialFromDate, initialToDate } = getCompanyKafkaQueryTimespan();
  return (
    <React.Fragment>
      <Helmet title={company_name ? `#${company_id} ${company_name}` : `Company #${company_id}`}></Helmet>
      <div className="company">
        <Stack direction="row" gap="small" alignItems="center" flexWrap="wrap">
          <h1 className="table__header company__titleRow">
            <Button
              type="button"
              kind="ghost"
              size="small"
              hideLabel
              className={isFavorite ? 'company__favoriteStar' : undefined}
              iconStart={isFavorite ? 'small-star' : 'small-star-outline'}
              title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              onClick={() => toggleFavoriteCompany({ companyId: company_id, companyName: company_name ?? undefined })}>
              {isFavorite ? 'Remove from favorites' : 'Add to favorites'}
            </Button>
            <span>{`#${company_id} ${company_name}`}</span>
          </h1>
          {isPrivilegedUser && (
            <Button
              type="button"
              kind="primary"
              size="small"
              onClick={() =>
                navigate(Routes.CompanyEdit.generatePath({ company_id, slug_company_name: companyNameSlug }))
              }>
              Edit
            </Button>
          )}
          {shipmentsHref !== undefined && (
            <Button
              type="button"
              kind="primary"
              size="small"
              onClick={() => {
                window.open(shipmentsHref, '_blank', 'noopener,noreferrer');
              }}>
              Open Shipments UI
            </Button>
          )}
          {getCompanyToursLinks({ companyId: company_id, isShipper: is_shipper, isCarrier: is_carrier }).map((link) => (
            <Button key={link.href} type="button" kind="primary" size="small" onClick={() => navigate(link.href)}>
              {link.label}
            </Button>
          ))}
          {tpaButton}
          {tpaTestButton}
          {tpaIntButton}
          {tpaDevButton}
        </Stack>

        <Stack justifyContent="space-between" className="company__metadata-container">
          <Stack
            direction="row"
            gap="xsmall"
            alignItems="center"
            flexWrap="wrap"
            justifyContent="center"
            className="company__flagsAndTags">
            {isPendingSignup !== null && isPendingSignup && <Pill kind="danger">Pending signup</Pill>}
            {is_carrier !== null && is_carrier && <Pill kind="info">Carrier</Pill>}
            {is_shipper !== null && is_shipper && <Pill kind="success">Shipper</Pill>}
            {is_pilot !== null && is_pilot && <Pill kind="neutral">Pilot</Pill>}
            {is_paying_customer !== null && is_paying_customer && <Pill kind="success">Paying</Pill>}
            {is_test !== null && is_test && <Pill kind="warning">Test</Pill>}
            {fms_integrated_at !== null && (
              <Tooltip content="Moment when the company first got vehicles over FMS integration OR when the first vehicle tracker was created for it">
                <Pill kind="neutral">
                  FMS integrated at <FormattedDate date={fms_integrated_at} />
                </Pill>
              </Tooltip>
            )}
            {signupCompletedAt !== null && (
              <Tooltip content="Moment when the company completed the signup process - consented to signing up to our platform">
                <Pill kind="neutral">
                  Signup completed at <FormattedDate date={signupCompletedAt} />
                </Pill>
              </Tooltip>
            )}
            <div className="company__tags">
              <TagsInput
                tags={tags}
                addTag={(tagName) =>
                  onAddCompanyTag({
                    variables: {
                      input: {
                        companyId: company.company_id,
                        tagName,
                      },
                    },
                  })
                }
                removeTag={(tagName) =>
                  onRemoveCompanyTag({
                    variables: {
                      input: {
                        companyId: company.company_id,
                        tagName,
                      },
                    },
                  })
                }
                availableTags={availableTags}
              />
            </div>
          </Stack>

          <CompanyIdentifiers identifiers={identifiers} companyId={company.company_id} />
        </Stack>

        <NavigationTabs>
          <NavigationTabsItem
            href={Routes.CompanyUsers.generatePath({ company_id, slug_company_name: companyNameSlug })}
            end={tabCount(tabCounts?.company?.users.count)}>
            Users
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyTelematicsIntegrations.generatePath({
              company_id,
              slug_company_name: companyNameSlug,
            })}
            end={tabCount(tabCounts?.telematicsIntegrations?.count)}>
            Integrations
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyPlatforms.generatePath({ company_id, slug_company_name: companyNameSlug })}
            end={tabCount(platforms.length)}>
            Platforms
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyCarrierPartners.generatePath({ company_id, slug_company_name: companyNameSlug })}
            end={tabCount(tabCounts?.company?.carriersConnection.count)}>
            Carriers
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyCustomerPartners.generatePath({ company_id, slug_company_name: companyNameSlug })}
            end={tabCount(tabCounts?.company?.customersConnection.count)}>
            Customers
          </NavigationTabsItem>
          <NavigationTabsItem
            href={`${Routes.Vehicles.generatePath({})}?company_id=${company_id}`}
            end={tabCount(tabCounts?.company?.totalVehiclesCount)}>
            Vehicles
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyRelationships.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            Relationships
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyVehicleGrants.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            Grants
          </NavigationTabsItem>
          {isPrivilegedUser && (
            <NavigationTabsItem
              href={Routes.CompanyAssets.generatePath({ company_id, slug_company_name: companyNameSlug })}>
              Assets
            </NavigationTabsItem>
          )}
          <NavigationTabsItem
            href={Routes.CompanyOnboarding.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            Onboarding
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyUserGroups.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            User groups
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyConfiguration.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            Configuration
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyFeatureFlags.generatePath({ company_id, slug_company_name: companyNameSlug })}
            end={tabCount(enabledFeatureFlagsCount)}>
            Feature flags
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyDashboards.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            Dashboards
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyKafka.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            Kafka
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyFileStorage.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            File storage
          </NavigationTabsItem>
          <NavigationTabsItem href={Routes.CompanyLem.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            LEM
          </NavigationTabsItem>
          <NavigationTabsItem
            href={Routes.CompanyAgreement.generatePath({ company_id, slug_company_name: companyNameSlug })}>
            Agreement
          </NavigationTabsItem>
        </NavigationTabs>
      </div>
      <KafkaContextProvider initialFromDate={initialFromDate} initialToDate={initialToDate}>
        <RouterRoutes>
          <Route path={Routes.CompanyUser.relativePath} element={<UserInCompanyContainer />} />
          <Route path={Routes.CompanyUsers.relativePath} element={<UserInCompanyListContainer />} />
          <Route
            path={Routes.CompanyTelematicsIntegrationCreate.relativePath}
            element={<CreateIntegrationContainer />}
          />
          <Route
            path={Routes.CompanyWebhookSubscriptionCreate.relativePath}
            element={<WebhookSubscriptionContainer />}
          />
          <Route path={Routes.CompanyWebhookSubscription.relativePath} element={<WebhookSubscriptionContainer />} />
          <Route path={Routes.CompanyTelematicsIntegration.relativePath} element={<IntegrationContainer />} />
          <Route path={Routes.CompanyTelematicsIntegrations.relativePath} element={<CompanyIntegrationsContainer />} />
          <Route path={Routes.CompanyPlatform.relativePath} element={<PlatformContainer />} />
          <Route path={Routes.CompanyPlatforms.relativePath} element={<PlatformListContainer />} />
          <Route path={Routes.CompanyRelationship.relativePath} element={<RelationshipContainer />} />
          <Route
            path={Routes.CompanyRelationships.relativePath}
            element={
              <RelationshipsTabs>
                <RelationshipsContainer />
              </RelationshipsTabs>
            }
          />
          <Route
            path={Routes.CompanyGroup.relativePath}
            element={
              <RelationshipsTabs>
                <CompanyGroupContainer companyName={company_name} />
              </RelationshipsTabs>
            }
          />
          {isPrivilegedUser && <Route path={Routes.CompanyAssets.relativePath} element={<CompanyAssetsContainer />} />}
          <Route path={Routes.CompanyOnboarding.relativePath} element={<CompanyOnboardingContainer />} />
          <Route path={Routes.CompanyConfiguration.relativePath} element={<CompanyConfigurationContainer />} />
          <Route path={Routes.CompanyFeatureFlags.relativePath} element={<CompanyFeatureFlagsContainer />} />
          <Route path={Routes.CompanyDashboards.relativePath} element={<CompanyDashboardsContainer />} />
          <Route path={Routes.CompanyKafka.relativePath} element={<CompanyKafkaTab />} />
          <Route path={Routes.CompanyFileStorage.relativePath} element={<CompanyFileStorageContainer />} />
          <Route path={Routes.CompanyLem.relativePath} element={<CompanyLemContainer />} />
          <Route path={Routes.CompanyAgreement.relativePath} element={<CompanyAgreementContainer />} />
          <Route path={Routes.CompanyUserGroup.relativePath} element={<CompanyUserGroupContainer />} />
          <Route path={Routes.CompanyUserGroups.relativePath} element={<CompanyUserGroupsContainer />} />
          <Route
            path={Routes.CompanyVehicleGrants.relativePath}
            element={<VehicleGrantListContainer company={company} />}
          />
          <Route path={Routes.CompanyCarrierPartners.relativePath} element={<MetricsContainer show="carriers" />} />
          <Route path={Routes.CompanyCustomerPartners.relativePath} element={<MetricsContainer show="customers" />} />
          <Route
            path={Routes.CompanyEdit.relativePath}
            element={
              <CompanyForm
                data={{ company }}
                routeProps={{ goBack: () => navigate(-1) }}
                updateCompany={updateCompany}
              />
            }
          />
          <Route
            index
            element={
              <Navigate
                to={Routes.CompanyUsers.generatePath({ company_id, slug_company_name: companyNameSlug })}
                replace
              />
            }
          />
        </RouterRoutes>
      </KafkaContextProvider>
    </React.Fragment>
  );
};

export const CompanyIdentifiers: React.FunctionComponent<{
  identifiers: CompanyIdentifierProps[];
  companyId: string;
}> = ({ identifiers, companyId }) => {
  return (
    <Stack justifyContent="center" columnGap="xsmall" className="id_labels">
      {identifiers.map((identifier, index) => (
        <CompanyIdentifier
          companyIdentifierId={identifier.companyIdentifierId}
          identifierType={identifier.identifierType}
          country={identifier.country}
          identifierValue={identifier.identifierValue}
          source={identifier.source}
          companyId={identifier.companyId}
          key={index}
        />
      ))}
      <CompanyIdentifierAddModal
        trigger={<Button type="button">{'\u2795'}</Button>}
        companyId={companyId}></CompanyIdentifierAddModal>
    </Stack>
  );
};

interface CompanyIdentifierProps {
  companyIdentifierId: string;
  identifierType: string;
  identifierValue: string;
  country: string | undefined;
  source: string;
  companyId: string;
}

export const CompanyIdentifier: React.FunctionComponent<CompanyIdentifierProps> = ({
  companyIdentifierId,
  identifierType,
  country,
  identifierValue,
  source,
  companyId,
}) => {
  return (
    <CompanyIdentifierModifyModal
      companyId={companyId}
      companyIdentifierId={companyIdentifierId}
      trigger={
        <Button className="companyIdentifierLabel" type="button">
          <Pill kind="neutral">
            {mapIdentifierType(identifierType)}: {identifierValue}
            {country ? ` · ${country}` : ''}
            {source ? ` · ${source}` : ''}
          </Pill>
        </Button>
      }></CompanyIdentifierModifyModal>
  );
};
